import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Typography,
  Button,
  Switch,
  Spinner,
  Alert,
  Chip,
} from '@material-tailwind/react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();
const funcGetConfig = httpsCallable(functions, 'getCirrusScrapeConfig', { timeout: 30_000 });
const funcSaveConfig = httpsCallable(functions, 'saveCirrusScrapeConfig', { timeout: 30_000 });
const funcGetLanes = httpsCallable(functions, 'getCirrusLaneStates', { timeout: 30_000 });
const funcToggleLane = httpsCallable(functions, 'cirrusToggleLane', { timeout: 30_000 });

const LANE_LABELS = {
  'zenrows-proxy': 'ZenRows (proxy)',
  'brightdata-proxy': 'BrightData (proxy)',
  'scrapingbee-api': 'ScrapingBee (API)',
  'zenrows-api': 'ZenRows (API)',
};

// Brokers whose listings appear on the Cirrus pre-owned feed. Each can be
// independently included/excluded from the scrape.
const BROKERS = [
  { key: 'cirrus_next', label: 'Cirrus Next', help: 'Factory listings hosted on cirrusaircraft.com (full data + images via the Cirrus API).' },
  { key: 'aerista', label: 'Aerista', help: 'Listings hosted on aerista.com — broker page is scraped for the photo gallery.' },
  { key: 'lone_mountain', label: 'Lone Mountain Aircraft', help: 'Listings hosted on lonemountainaircraft.com — broker page is scraped for the photo gallery.' },
];

export default function CirrusScrapeConfigPage() {
  const [config, setConfig] = useState({ cirrus_next: true, aerista: true, lone_mountain: true });
  const [lanes, setLanes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadLanes = async () => {
    try {
      const res = await funcGetLanes();
      if (res.data?.success) setLanes(res.data.lanes);
    } catch (e) {
      // non-fatal — broker config still usable
      console.warn('could not load lanes', e.message);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await funcGetConfig();
        if (res.data?.success && res.data.config) {
          setConfig((prev) => ({ ...prev, ...res.data.config }));
        } else {
          setError(res.data?.error || 'Could not load config');
        }
        await loadLanes();
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleLane = async (lane, action) => {
    setError(null);
    try {
      const res = await funcToggleLane({ lane, action });
      if (res.data?.success) {
        setLanes(res.data.lanes);
      } else {
        setError(res.data?.error || 'Could not update lane');
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const lanePaused = (s) => {
    if (!s) return false;
    if (s.state === 'PAUSED') {
      if (!s.pauseUntil) return true;
      const ms = s.pauseUntil._seconds ? s.pauseUntil._seconds * 1000 : new Date(s.pauseUntil).getTime();
      return Date.now() < ms;
    }
    return false;
  };

  const toggle = (key) => {
    setSuccess(null);
    setConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await funcSaveConfig({ config });
      if (res.data?.success) {
        setConfig((prev) => ({ ...prev, ...res.data.config }));
        setSuccess('Saved. The next scheduled Cirrus scrape will use these settings.');
      } else {
        setError(res.data?.error || 'Could not save config');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 mb-12 flex flex-col items-center gap-6">
      <Card className="w-full max-w-2xl">
        <CardHeader floated={false} shadow={false} className="m-0 p-6">
          <Typography variant="h5" color="blue-gray">Cirrus Scrape — Brokers</Typography>
          <Typography className="text-sm text-gray-600 mt-1">
            Choose which brokers are included when scraping the Cirrus pre-owned inventory.
          </Typography>
        </CardHeader>
        <CardBody className="pt-0">
          {error && <Alert color="red" className="mb-4">{error}</Alert>}
          {success && <Alert color="green" className="mb-4">{success}</Alert>}

          {loading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : (
            <>
              <div className="flex flex-col gap-5">
                {BROKERS.map((b) => (
                  <div key={b.key} className="flex items-start justify-between gap-4 border-b border-gray-100 pb-4">
                    <div>
                      <Typography variant="h6" color="blue-gray">{b.label}</Typography>
                      <Typography className="text-xs text-gray-600">{b.help}</Typography>
                    </div>
                    <Switch
                      checked={config[b.key] !== false}
                      onChange={() => toggle(b.key)}
                      crossOrigin=""
                    />
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={save} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <Card className="w-full max-w-2xl">
        <CardHeader floated={false} shadow={false} className="m-0 p-6">
          <Typography variant="h5" color="blue-gray">Cirrus Scrape — Fetch Lanes</Typography>
          <Typography className="text-sm text-gray-600 mt-1">
            Proxy/provider lanes are tried in priority order with automatic failover. A lane auto-pauses after
            repeated failures (circuit breaker); use Unpause to bring it back immediately.
          </Typography>
        </CardHeader>
        <CardBody className="pt-0">
          {!lanes ? (
            <Typography className="text-sm text-gray-600">No lane data.</Typography>
          ) : (
            <div className="flex flex-col gap-4">
              {Object.entries(lanes)
                .sort((a, b) => (a[1].priority || 0) - (b[1].priority || 0))
                .map(([key, s]) => {
                  const paused = lanePaused(s);
                  return (
                    <div key={key} className="flex items-center justify-between gap-4 border-b border-gray-100 pb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Typography variant="h6" color="blue-gray">{LANE_LABELS[key] || key}</Typography>
                          <Chip size="sm" variant="ghost" value={`#${s.priority}`} />
                          {s.enabled === false ? (
                            <Chip size="sm" color="gray" value="disabled" />
                          ) : paused ? (
                            <Chip size="sm" color="amber" value="paused" />
                          ) : (
                            <Chip size="sm" color="green" value="healthy" />
                          )}
                        </div>
                        <Typography className="text-xs text-gray-600">
                          ✓ {s.successCount || 0} · ✗ {s.failureCount || 0}
                          {s.lastError ? ` · last error: ${String(s.lastError).slice(0, 60)}` : ''}
                        </Typography>
                      </div>
                      <div className="flex items-center gap-2">
                        {paused && s.enabled !== false && (
                          <Button size="sm" variant="outlined" onClick={() => toggleLane(key, 'unpause')}>
                            Unpause
                          </Button>
                        )}
                        <Switch
                          checked={s.enabled !== false}
                          onChange={() => toggleLane(key, s.enabled === false ? 'enable' : 'disable')}
                          crossOrigin=""
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
